/**
 * Formatting shared by the detail tabs. `en-IN` throughout — the console is
 * operated from India and the product's data is Indian.
 */

const DATE = new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });

const DATE_TIME = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export function formatDate(iso: string): string {
  return DATE.format(new Date(iso));
}

/** A calendar day, read as itself: the same date in every browser zone. */
const LOCAL_DATE = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: '2-digit',
  timeZone: 'UTC',
});

/**
 * A `YYYY-MM-DD` day from the API (a gym's trial dates, a member's join day).
 * `formatDate` would read it as midnight UTC and, west of UTC, show the day
 * before; this formats the calendar day it names.
 */
export function formatLocalDate(date: string): string {
  return LOCAL_DATE.format(new Date(`${date}T00:00:00Z`));
}

const INR_WHOLE = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});
const INR_PAISE = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * An amount the API sends in integer paise, as rupees with Indian grouping:
 * 3598800 is ₹35,988, 238850 is ₹2,388.50. Whole rupees drop the decimals.
 */
export function formatInrPaise(paise: number): string {
  return paise % 100 === 0 ? INR_WHOLE.format(paise / 100) : INR_PAISE.format(paise / 100);
}

export function formatDateTime(iso: string): string {
  return DATE_TIME.format(new Date(iso));
}

/** Turns a wire enum into something readable: `non_vegetarian` → `non vegetarian`. */
export function humanise(value: string): string {
  return value.replace(/_/g, ' ');
}

export function formatPercent(rate: number): string {
  return `${String(Math.round(rate * 100))}%`;
}

/**
 * How long something took, in the coarsest unit that still carries information.
 *
 * Milliseconds are the wire format and are unreadable in a table; "42.3s" and
 * "2m 05s" are what a reader compares between rows. Null is a different fact
 * from zero — the thing has not finished, or was never measured.
 */
export function formatDuration(durationMs: number | null): string | null {
  if (durationMs === null) return null;
  if (durationMs < 1000) return `${String(durationMs)}ms`;

  const totalSeconds = durationMs / 1000;
  if (totalSeconds < 60) return `${totalSeconds.toFixed(1)}s`;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${String(minutes)}m ${String(seconds).padStart(2, '0')}s`;
}

/**
 * How long ago, in the coarsest unit that is still true.
 *
 * Deliberately vague past a week: "23 days ago" invites arithmetic that the
 * exact date, shown beside it, answers better.
 */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const elapsedMs = now.getTime() - new Date(iso).getTime();
  const days = Math.floor(elapsedMs / 86_400_000);

  if (days < 0) return 'just now';
  if (days === 0) {
    const hours = Math.floor(elapsedMs / 3_600_000);
    if (hours === 0) return 'within the hour';
    return `${String(hours)}h ago`;
  }
  if (days === 1) return 'yesterday';
  if (days < 7) return `${String(days)}d ago`;
  if (days < 30) return `${String(Math.floor(days / 7))}w ago`;

  return `${String(Math.floor(days / 30))}mo ago`;
}
